import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

import Header from '../components/Header';
import Footer from '../components/Footer';

// ============================================================
// LISTA DE ESPERA — Tela para usuários logados (não-admin)
// Aparece quando o usuário faz login mas o app ainda não lançou
// ============================================================

function ListaEspera() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        if (!currentUser) {
          navigate('/login');
          return;
        }
        setUser(currentUser);

        // Admin passa direto
        const { data: profile } = await supabase
          .from('profiles')
          .select('plano')
          .eq('user_id', currentUser.id)
          .single();

        if (profile?.plano === 'admin') {
          navigate('/');
          return;
        }
      } catch (err) {
        console.error('Erro ao buscar dados:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const primeiroNome = user?.user_metadata?.nome
    ? user.user_metadata.nome.split(' ')[0]
    : user?.email?.split('@')[0] || '';

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <svg className="animate-spin h-10 w-10 text-primary mx-auto mb-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <p className="text-muted-foreground">Carregando...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <section className="flex-1 flex items-center justify-center py-16 px-4">
        <div className="max-w-lg w-full text-center">
          {/* Bruno */}
          <div className="relative w-28 h-28 mx-auto mb-6">
            <div className="absolute -inset-4 bg-primary/10 rounded-full blur-2xl" />
            <img
              src="/images/brand/bruno-mascot.png"
              alt="Bruno, mascote do QuantoCustaObra"
              className="relative w-full h-full object-contain animate-float"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>

          <h1 className="font-display font-extrabold text-3xl md:text-4xl text-secondary mb-4">
            Falta pouco{primeiroNome ? `, ${primeiroNome}` : ''}! 🏗️
          </h1>

          <p className="text-muted-foreground text-lg mb-8 leading-relaxed max-w-md mx-auto">
            Estamos finalizando os últimos ajustes do QuantoCustaObra.
            Você será avisado por email assim que liberarmos o acesso.
          </p>

          {/* Progresso do desenvolvimento */}
          <div className="bg-card rounded-2xl shadow-card p-8 mb-8 text-left">
            <h3 className="font-display font-bold text-secondary text-center mb-6">
              O que estamos preparando:
            </h3>

            <div className="space-y-4">
              {[
                { label: 'Motor de leitura de plantas com IA', pct: 90 },
                { label: 'Orçamento completo', pct: 95 },
                { label: 'Quantitativo de materiais', pct: 85 },
                { label: 'Exportação PDF e Excel', pct: 90 },
                { label: 'Sistema de pagamentos', pct: 100 },
              ].map((item) => (
                <div key={item.label}>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="text-secondary font-medium">{item.label}</span>
                    <span className="font-semibold text-muted-foreground">{item.pct}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2.5">
                    <div
                      className={`h-2.5 rounded-full transition-all duration-1000 ${
                        item.pct === 100 ? 'bg-accent' : 'bg-primary'
                      }`}
                      style={{ width: `${item.pct}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Email */}
          <div className="bg-muted/50 rounded-xl p-4 mb-8">
            <p className="text-sm text-muted-foreground">
              Avisaremos em <span className="font-semibold text-secondary">{user?.email}</span> quando estiver pronto.
            </p>
          </div>

          {/* Compartilhar */}
          <p className="text-sm text-muted-foreground mb-3">
            Conhece alguém que vai construir? Indica pra gente!
          </p>
          <a
            href={`https://wa.me/?text=${encodeURIComponent('O QuantoCustaObra é uma IA que faz orçamento de obra direto da planta baixa! Tá em pré-lançamento: https://quantocustaobra.com.br')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-cta px-6 py-2.5 text-sm no-underline inline-flex items-center gap-2"
          >
            📱 Compartilhar no WhatsApp
          </a>

          {/* Logout */}
          <div className="mt-8 pt-6 border-t border-border">
            <button
              onClick={handleLogout}
              className="text-sm text-muted-foreground hover:text-secondary transition-colors"
            >
              Sair da conta
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default ListaEspera;
